import { useEffect } from 'preact/hooks';
import {
  config,
  digitalView,
  digitalEntryReady,
  digitalChatMode,
  messages,
  appendMessage,
  appendDelta,
  appendFullText,
  updateMessage,
  aiThinking,
  videoState,
  activeLeadCard,
  lastLeadCard,
  pendingLeadCard,
  flushPendingLeadCardFor,
  hydrateFromHistory,
  restoreActiveTurn,
  type LeadCardData,
} from '../../store/widget-store';
import type {
  LeadCardShowPayload,
  SystemNoticePayload,
  AiAudioPayload,
} from '@digital-cs/shared';
import { useI18n } from '../../hooks/use-i18n';
import type {
  AiStreamStartPayload,
  AiStreamDeltaPayload,
  AiStreamEndPayload,
  TurnActivePayload,
} from '../../types';
import { getInitialSession, getWsClient } from '../../services/ws-client';
import { getAudioPlayer } from '../../services/audio-player';
import { VideoStateMachine } from '../../services/video-state-machine';
import { MinimizedBubble } from './MinimizedBubble';
import { ImmersiveShell } from './ImmersiveShell';

/**
 * 无音频降级:ai.stream.end 后仍未收到 ai.audio → 直接显全文并回 idle(TTS 未启用/失败时兜底)。
 * 取 15s 以容纳整段 TTS 合成+网络(过短会在慢 TTS 下误降级);即便误降级,音频到达时 onStart 的
 * sm.audioStart() 仍会无条件恢复 speaking + 重启打字机,不会卡在 idle。
 */
const NO_AUDIO_DEGRADE_MS = 15000;

/**
 * 在飞回合 watchdog:刷新/重连经 turn.active 恢复忙碌态后,若 BE 重启等极端情况
 * 导致 ai.stream.end 永远不来,90s 后自动解锁闸门并收尾 streaming 气泡,防永久卡死。
 * 取 90s 以容纳慢模型首字(意图+主回答前置串行可能数十秒);流式一旦开始,end 到达即撤。
 */
const TURN_WATCHDOG_MS = 90_000;

// 打字机 rAF / 降级定时器句柄(module 级,按 messageId 管理)
const typewriterRafs = new Map<string, number>();
const noAudioTimers = new Map<string, ReturnType<typeof setTimeout>>();

// 待弹留资卡统一放 store 的 pendingLeadCard signal(与 web 线共用):
// lead.card.show 到达时,若数字人还在口播这段「铺垫话术」(对应 AI 消息
// streaming===true),先把卡片暂存并绑定到该消息,等其打字机结束(finishTypewriter / 非语音
// stream.end 落 streaming:false)再真正弹出,避免卡片在话术念到一半时就糊脸盖住。
// BE 在 pseudoStream(文本流)结束后立刻 emitLeadCard,而音频(ai.audio)是 fire-and-forget 晚到,
// 故 lead.card.show 必然早于口播结束抵达 —— 时序必须前端兜住。绑定到具体 messageId 而非全局标志:
// 重置/换会话后旧 anchor 消息不存在,卡片自然不会误弹,只会被下一次 show 覆盖或卸载时清掉。
// 暂存期间 ChatPane 在锚消息下方渲染可点 pill(openPendingLeadCard 提前展开),与 web 线一致。

function fullTextOf(messageId: string): string {
  const m = messages.value.find((x) => x.id === messageId);
  return m?.fullText ?? m?.text ?? '';
}

function cancelTypewriter(messageId: string): void {
  const r = typewriterRafs.get(messageId);
  if (r != null) {
    cancelAnimationFrame(r);
    typewriterRafs.delete(messageId);
  }
}

/** 按音频时长把 fullText 逐字显出到 displayText(rAF 推进,以 onStart 为锚)。 */
function startTypewriter(messageId: string, durationMs: number): void {
  cancelTypewriter(messageId);
  const full = fullTextOf(messageId);
  const total = full.length;
  if (total === 0) {
    updateMessage(messageId, { displayText: '' });
    return;
  }
  const dur = Math.max(300, durationMs || 1500);
  const startedAt = performance.now();
  const tick = (now: number) => {
    const ratio = Math.min(1, (now - startedAt) / dur);
    const shown = Math.round(total * ratio);
    updateMessage(messageId, { displayText: full.slice(0, shown) });
    if (ratio < 1) {
      typewriterRafs.set(messageId, requestAnimationFrame(tick));
    } else {
      typewriterRafs.delete(messageId);
    }
  };
  typewriterRafs.set(messageId, requestAnimationFrame(tick));
}

function finishTypewriter(messageId: string): void {
  cancelTypewriter(messageId);
  updateMessage(messageId, { displayText: fullTextOf(messageId), streaming: false });
  // 口播+打字机结束 → 若有暂存的留资卡绑定到这条消息,现在才弹出。
  flushPendingLeadCardFor(messageId);
}

function clearNoAudioTimer(messageId: string): void {
  const t = noAudioTimers.get(messageId);
  if (t != null) {
    clearTimeout(t);
    noAudioTimers.delete(messageId);
  }
}

/**
 * 数字人客服常驻宿主 —— 只要 cfg.type==='digital' 就全程挂载。
 *
 * 集中订阅 SSE(ai.stream.* / ai.audio / system.notice / lead.card.show)+ 视频状态机 + 历史还原,
 * 使其不随 digitalView(minimized/welcome/chat)切换而卸载重连。
 *
 * 语音/打字机(整段一次合成):
 *  - ai.stream.delta 累积到 fullText(不直接渲染,气泡先显三点);
 *  - 收到 ai.audio 后入队播放,音频开始(onStart)起按时长逐字显出 + 视频切 speaking,
 *    音频结束(onEnded)显全文 + 回 idle;
 *  - ai.stream.end 起 15s 无音频降级定时器:TTS 未启用/失败时直接显全文回 idle,气泡不会永停三点。
 *  - 人工模式(digitalChatMode==='human')不播 AI 语音(真人消息走 IM,不做 TTS)。
 */
export function DigitalRoot() {
  const { t } = useI18n();

  useEffect(() => {
    const client = getWsClient();
    const sm = new VideoStateMachine('idle');
    let cancelled = false;

    // 在飞回合 watchdog:恢复忙碌态后若回合事件永远不来(BE 重启等),超时自动解锁防卡死
    let turnWatchdog: ReturnType<typeof setTimeout> | null = null;
    const clearTurnWatchdog = () => {
      if (turnWatchdog) {
        clearTimeout(turnWatchdog);
        turnWatchdog = null;
      }
    };
    const armTurnWatchdog = () => {
      clearTurnWatchdog();
      turnWatchdog = setTimeout(() => {
        turnWatchdog = null;
        aiThinking.value = false;
        for (const m of messages.value) {
          if (m.role === 'ai' && m.streaming) {
            updateMessage(m.id, { streaming: false });
            flushPendingLeadCardFor(m.id); // 有暂存留资卡锚定它则弹出
          }
        }
      }, TURN_WATCHDOG_MS);
    };
    // turn.active / session.activeTurn 统一入口:恢复气泡快照 + 忙碌闸门 + watchdog
    const applyActiveTurn = (turn: TurnActivePayload) => {
      restoreActiveTurn(turn, {
        audioOn: config.value?.audioEnabled === true,
      });
      armTurnWatchdog();
    };

    void (async () => {
      await client.connect();
      if (cancelled) return;
      const sess = getInitialSession();
      if (sess) {
        // 有在飞回合时延弹历史还原卡:挂暂存位,回合流式结束再弹(不压在回复内容上方)
        hydrateFromHistory(sess.history, sess.pendingCards, t('panel.leadReopenHint'), {
          deferLeadCard: !!sess.activeTurn,
        });
        // 刷新恢复:有在飞回合则恢复忙碌闸门 + 快照气泡
        // (SSE 建连时 BE 还会补一帧 turn.active,restoreActiveTurn 幂等,两处同源不冲突)
        if (sess.activeTurn) applyActiveTurn(sess.activeTurn);
      }
    })();
    videoState.value = sm.current();
    const offSm = sm.on((s) => {
      videoState.value = s;
    });

    // 语音播放回调驱动:onStart→speaking+打字机起;onEnded/onError→idle+显全文
    const player = getAudioPlayer();
    player.configure({
      onStart: (cue) => {
        // 音频真开播:无条件切 speaking(即便已被降级误切走/乱序),保证音频期间视频口播
        sm.audioStart();
        startTypewriter(cue.messageId, cue.durationMs);
      },
      onEnded: (cue) => {
        sm.audioEnded();
        finishTypewriter(cue.messageId);
      },
      onError: (cue) => {
        sm.audioEnded();
        finishTypewriter(cue.messageId);
      },
    });

    // 是否启用实时语音(后端 TTS 开启且 type=digital);未启用走 web 式实时流式回显。
    const audioOn = () => config.value?.audioEnabled === true;
    // 未启用语音时:每条回答首个 delta 触发 listening→speaking(模拟口型)
    let firstDeltaForMessage: string | null = null;

    const offTurnActive = client.on('turn.active', (raw) => {
      applyActiveTurn(raw as TurnActivePayload);
    });

    const offStart = client.on('ai.stream.start', (raw) => {
      const p = raw as AiStreamStartPayload;
      aiThinking.value = false;
      sm.streamStart();
      firstDeltaForMessage = p.messageId;
      const existing = messages.value.find((m) => m.id === p.messageId);
      if (existing) {
        updateMessage(p.messageId, { streaming: true });
      } else {
        appendMessage({
          id: p.messageId,
          role: 'ai',
          text: '',
          fullText: '',
          ts: Date.now(),
          streaming: true,
        });
      }
    });
    const offDelta = client.on('ai.stream.delta', (raw) => {
      const p = raw as AiStreamDeltaPayload;
      if (audioOn()) {
        // 启用语音:累积到 fullText,不实时显字(等 ai.audio 驱动逐字)
        appendFullText(p.messageId, p.text);
      } else {
        // 未启用语音:按 web 式实时流式回显文本(首个 delta 切 speaking 模拟口型)
        if (firstDeltaForMessage === p.messageId) {
          sm.ttsChunk();
          firstDeltaForMessage = null;
        }
        appendDelta(p.messageId, p.text);
      }
    });
    const offEnd = client.on('ai.stream.end', (raw) => {
      const p = raw as AiStreamEndPayload;
      aiThinking.value = false;
      clearTurnWatchdog(); // 回合正常收尾,撤 watchdog
      if (!audioOn()) {
        // 未启用语音:立即结流回 idle(无 5s 等待)。文本已随 delta 实时显完,可立刻放行暂存的留资卡。
        updateMessage(p.messageId, { streaming: false });
        sm.streamEnd();
        flushPendingLeadCardFor(p.messageId);
        return;
      }
      // 启用语音:不立即结流/切 idle —— 等 ai.audio 播放结束驱动;起 5s 无音频降级兜底
      clearNoAudioTimer(p.messageId);
      const timer = setTimeout(() => {
        noAudioTimers.delete(p.messageId);
        const m = messages.value.find((x) => x.id === p.messageId);
        if (m?.audio?.received) return; // 音频已到,交给音频链路收尾
        finishTypewriter(p.messageId);
        sm.streamEnd();
      }, NO_AUDIO_DEGRADE_MS);
      noAudioTimers.set(p.messageId, timer);
    });
    const offAudio = client.on('ai.audio', (raw) => {
      const p = raw as AiAudioPayload;
      updateMessage(p.messageId, {
        audio: { mimeType: p.mimeType, durationMs: p.durationMs, received: true },
      });
      clearNoAudioTimer(p.messageId);
      if (digitalChatMode.value !== 'ai') return; // 人工模式不播 AI 语音
      player.enqueue({
        messageId: p.messageId,
        base64: p.base64,
        mimeType: p.mimeType,
        durationMs: p.durationMs,
      });
    });
    const offNotice = client.on('system.notice', (raw) => {
      const p = raw as SystemNoticePayload;
      appendMessage({
        id: `m-sys-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        role: 'system',
        text: p.text,
        ts: Date.now(),
      });
    });
    const offLeadCard = client.on('lead.card.show', (raw) => {
      const p = raw as LeadCardShowPayload;
      const data: LeadCardData = {
        fields: p.fields,
        prompt: p.prompt,
        anchorMsgId: p.messageId,
      };
      // 锚定 BE 指定的触发消息(本轮主回答):还在口播(streaming===true)就暂存,
      // 等其打字机结束再弹(见 flushPendingLeadCardFor);文本已显完则立即弹。
      // 连续提问两轮并发时也不会再错绑到后一轮的口播消息(旧逻辑是 find 任意 streaming 消息)。
      // 旧 BE 不带 messageId 时退化为旧逻辑兜底。
      const anchor = p.messageId
        ? messages.value.find((m) => m.id === p.messageId)
        : undefined;
      const speaking =
        anchor ??
        messages.value.find((m) => m.role === 'ai' && m.streaming === true);
      if (anchor && !anchor.streaming) {
        // 锚点消息已显完(常见于非语音链路:stream.end 与 show 几乎同帧) → 直接弹
        activeLeadCard.value = data;
        lastLeadCard.value = data;
      } else if (speaking) {
        pendingLeadCard.value = { anchorMsgId: speaking.id, data };
      } else {
        activeLeadCard.value = data;
        lastLeadCard.value = data;
      }
    });
    return () => {
      cancelled = true;
      offStart();
      offDelta();
      offEnd();
      offAudio();
      offNotice();
      offLeadCard();
      offTurnActive();
      offSm();
      clearTurnWatchdog();
      player.stopAll();
      typewriterRafs.forEach((r) => cancelAnimationFrame(r));
      typewriterRafs.clear();
      noAudioTimers.forEach((tm) => clearTimeout(tm));
      noAudioTimers.clear();
      pendingLeadCard.value = null;
    };
  }, []);

  const cfg = config.value;
  if (!cfg) return null;

  // 悬浮球入口等海报预载落定(digitalEntryReady)再显示:首访点开即见海报、无白屏窗口。
  // 程序化唤起(DCSWidget.open / 事件 / fullPage / defaultWindow=max)直接把 digitalView
  // 切出 minimized,不经此门控 —— 小程序整页 H5 行为因此保持不变。
  return digitalView.value === 'minimized' ? (
    digitalEntryReady.value ? (
      <MinimizedBubble />
    ) : null
  ) : (
    <ImmersiveShell />
  );
}
